import React, { createContext, useContext } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import i18n from '../i18n';

// 支援的語言列表
export const supportedLanguages = [
  {
    code: 'zh',
    name: '繁體中文',
    nativeName: '繁體中文'
  },
  {
    code: 'en',
    name: 'English',
    nativeName: 'English'
  }
];

// 創建語言Context
const LanguageContext = createContext();

// 語言Provider組件
export const LanguageProvider = ({ children }) => {
  const { t, i18n: i18nInstance } = useI18nTranslation();

  const currentLanguage = i18nInstance.language || 'zh';

  // 切換語言
  const changeLanguage = async (languageCode) => {
    if (languageCode === currentLanguage) {
      return;
    }

    try {
      await i18nInstance.changeLanguage(languageCode);
      localStorage.setItem('language', languageCode);

      // 觸發自定義事件，通知其他組件語言已變更
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { language: languageCode }
      }));
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // 獲取當前語言資訊
  const getCurrentLanguageInfo = () => {
    return supportedLanguages.find(lang => lang.code === currentLanguage);
  };

  // 檢查是否為指定語言
  const isLanguage = (languageCode) => {
    return currentLanguage === languageCode;
  };

  // 檢查是否為中文
  const isChinese = () => {
    return currentLanguage === 'zh';
  };

  // 檢查是否為英文
  const isEnglish = () => {
    return currentLanguage === 'en';
  };

  // Context值
  const contextValue = {
    // 狀態
    currentLanguage,
    isLoading: false,
    supportedLanguages,

    // 方法
    changeLanguage,
    t,
    getCurrentLanguageInfo,
    isLanguage,
    isChinese,
    isEnglish
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// 自定義Hook來使用語言Context
export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

// 便捷的翻譯Hook - 直接使用 react-i18next
export const useTranslation = () => {
  const { t, i18n: i18nInstance } = useI18nTranslation();

  return {
    t,
    i18n: {
      language: i18nInstance.language,
      changeLanguage: i18nInstance.changeLanguage
    }
  };
};

export default LanguageContext;
